<script setup lang="ts">
import {
  getCategoryRoute,
  getSmallestThumbnailUrl,
  getTranslatedProperty,
} from "@shopware/helpers";
import type { Schemas } from "#shopware";
// importing dummy image
import thumbnailPlaceholder from '../../assets/images/mysofabed-placeholder-img.png';
import SharedImage from "~/components/shared/SharedImage.vue";

type NavigationElement = Schemas["Category"] & {
  activeClass?: boolean;
};

const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const { t } = useI18n();

const props = defineProps<{
  navigationElementChildren: Array<NavigationElement>;
  lastElement?: boolean;
  isMenuOpen?: boolean;
  parentCategory?: NavigationElement;
}>();

const emits = defineEmits<{
  (
    e: "updateActiveClass",
    navigationId: string,
    parentId: string | undefined,
  ): void;
  (e: "focusoutLastElement", lastElement: boolean): void;
  (e: "closeMenu"): void;
}>();

const emitUpdateActiveClass = (
  navigationId: string,
  parentId: string | undefined,
) => {
  emits("updateActiveClass", navigationId, parentId);
};

// Add computed property for delay classes
const getDelayClass = (index: number) => {
  // Using predefined delay classes that Tailwind will generate
  const delays: Record<number, string> = {
    0: 'delay-[25ms]',
    1: 'delay-[50ms]',
    2: 'delay-[75ms]',
    3: 'delay-[100ms]',
    4: 'delay-[125ms]',
    5: 'delay-[150ms]',
    6: 'delay-[175ms]',
    7: 'delay-[200ms]',
    8: 'delay-[225ms]',
    9: 'delay-[250ms]',
    10: 'delay-[275ms]',
    11: 'delay-[300ms]',
    12: 'delay-[325ms]'
  };
  return delays[index % 12] || 'delay-[25ms]'; // fallback to smallest delay
};

const closeMenu = () => {
  emits("closeMenu");
};

// Once the menu opens for the first time, keep images mounted so the browser
// cache handles subsequent opens without re-fetching.
const hasBeenOpened = ref(false);
watch(
  () => props.isMenuOpen,
  (val: boolean | undefined) => {
    if (val) hasBeenOpened.value = true;
  },
);

// Helper function to check if category is showroom
const isShowroomCategory = (category: NavigationElement | undefined): boolean => {
  if (!category) return false;
  const categoryName = getTranslatedProperty(category, "name")?.toLowerCase() || "";
  return categoryName.includes("showroom");
};

// Computed property for "All products" item
const allProductsItem = computed(() => {
  if (!props.parentCategory) return null;
  // Exclude showroom category
  if (isShowroomCategory(props.parentCategory)) return null;
  return {
    id: `${props.parentCategory.id}-all`,
    name: `${t('listing.allProducts')} ${getTranslatedProperty(props.parentCategory, "name")}`,
    media: props.parentCategory.media,
    parentId: props.parentCategory.parentId,
    activeClass: false,
  };
});
</script>

<template>
  <!-- "All products" item for parent category -->
  <template v-if="allProductsItem">
    <div 
      class="category-item overflow-hidden col-span-1 opacity-0 transition-all duration-300 ease-in-out group-[.menu-item-open]:opacity-100"
      :class="getDelayClass(0)"
    >
      <NuxtLink
        role="menuitem"
        :to="formatLink(getCategoryRoute(parentCategory!))"
        :target="
          parentCategory?.externalLink || parentCategory?.linkNewTab ? '_blank' : ''
        "
        :class="{
          'link-active': allProductsItem.activeClass,
        }"
        class="block group/link"
        :tabindex="0"
        @click.stop="emitUpdateActiveClass(parentCategory!.id, parentCategory!.parentId); closeMenu()"
      >
        <div
          class="prodcut-thumb aspect-[16/9] flex items-center justify-center relative"
        >
          <div class="min-h-auto md:min-h-[80px] lg:min-h-auto w-full">
            <template v-if="hasBeenOpened">
              <template v-if="allProductsItem.media?.url">
                <SharedImage
                  :src="allProductsItem.media.url"
                  imgClass="w-auto mx-auto max-h-[80px] md:max-h-[100px]"
                  :height="allProductsItem.media.metaData?.height || 160"
                  :width="allProductsItem.media.metaData?.width || 375"
                  loading="lazy"
                  :alt="allProductsItem.name ?? $t('mysofbed')"
                  :sizes="{ desktop: '211x90', tablet: '211x90', mobile: '197x84' }"
                  :mimeType="allProductsItem.media.mimeType"
                />
              </template>
              <template v-else>
                <img
                  :src="thumbnailPlaceholder"
                  class="w-auto mx-auto max-h-[80px] md:max-h-[100px]"
                  width="210"
                  height="90"
                  :alt="allProductsItem.name ?? $t('mysofbed')"
                  loading="lazy"
                />
              </template>
            </template>
            <template v-else>
              <div class="w-full h-full bg-grey-100 rounded animate-pulse" />
            </template>
          </div>
        </div>
        <span
          class="title group-hover/link:text-primary group-hover/link:underline transition-colors duration-300 ease-in-out block text-sm md:text-base mt-2 text-wrap leading-[18px] min-h-auto md:min-h-[72px] lg:min-h-auto"
          > {{ allProductsItem.name }}</span
        >
      </NuxtLink>
    </div>
  </template>
  
  <template
    v-for="(childElement, index) in navigationElementChildren"
    :key="childElement.id"
  >
    <div 
      class="category-item overflow-hidden col-span-1 opacity-0 transition-all duration-300 ease-in-out group-[.menu-item-open]:opacity-100"
      :class="getDelayClass(allProductsItem ? index + 1 : index)"
    >
      <NuxtLink
        role="menuitem"
        :to="formatLink(getCategoryRoute(childElement))"
        :target="
          childElement.externalLink || childElement.linkNewTab ? '_blank' : ''
        "
        :class="{
          'link-active': childElement.activeClass,
          'pointer-events-none cursor-default': childElement?.type === 'folder'
        }"
        class="block group/link"
        :tabindex="0"
        @click.stop="emitUpdateActiveClass(childElement.id, childElement.parentId); closeMenu()"
        @focusout="
          $props.lastElement === true &&
          navigationElementChildren &&
          navigationElementChildren.length - 1 === index
            ? emits('focusoutLastElement', true)
            : null
        "
      >
        <div
          class="prodcut-thumb aspect-[16/9] flex items-center justify-center relative"
        >
        <div class="min-h-auto md:min-h-[80px] lg:min-h-auto w-full">
          <template v-if="hasBeenOpened">
              <template v-if="childElement?.media?.url">
                <SharedImage
                  :src="childElement.media.url"
                  imgClass="w-auto mx-auto max-h-[80px] md:max-h-[100px]"
                  :height="childElement.media.metaData?.height || 160"
                  :width="childElement.media.metaData?.width || 375"
                  loading="lazy"
                  :alt="childElement.name ?? $t('mysofbed')"
                  :sizes="{ desktop: '211x90', tablet: '211x90', mobile: '197x84' }"
                  :mimeType="childElement.media.mimeType"
                />
              </template>
              <template v-else>
                <img
                  :src="thumbnailPlaceholder"
                  class="w-auto mx-auto max-h-[80px] md:max-h-[100px]"
                  :alt="getTranslatedProperty(childElement, 'name') ?? $t('mysofbed')"
                  loading="lazy"
                />
              </template>
            </template>
            <template v-else>
              <div class="w-full h-full bg-grey-100 rounded animate-pulse" />
            </template>
          </div>
        </div>
        <span
          class="title group-hover/link:text-primary group-hover/link:underline transition-colors duration-300 ease-in-out block text-sm md:text-base mt-2 text-wrap leading-[18px]"
          > {{ getTranslatedProperty(childElement, "name") }}</span
        >
      </NuxtLink>
      <template
        v-if="childElement.children && childElement.children.length > 0"
      >
        <LayoutTopNavigationRecursive
          :navigation-element-children="childElement.children"
          :parent-category="childElement"
          @update-active-class="
            emitUpdateActiveClass(childElement.id, childElement.parentId)
          "
        />
      </template>
    </div>
  </template>
</template>